/**
 * The sign-in screen, and the failure states the old one never showed.
 *
 * Previously the only feedback here was the string "signing in…", which stayed
 * on screen forever whenever Clerk failed to load. Every state now says what
 * happened and offers the next action.
 */
import { useState, type FormEvent } from 'react';

import { useAuth } from '@/auth/AuthContext';
import { userFacingError } from '@/api/client';
import { BrandMark } from '@/components/BrandMark';
import bakery from '@/assets/gate/bakery.webp';
import lantern from '@/assets/gate/lantern.webp';
import rooftop from '@/assets/gate/rooftop.webp';

/**
 * What the product makes, shown before it asks for anything: three prepared
 * scenes and a line of dialogue from example campaigns, laid on the table.
 * Decorative, so hidden from assistive technology; the form carries the page.
 */
function GateArt() {
  return (
    <div className="gate__art" aria-hidden="true">
      <div className="gate__stage">
        <figure className="gate__frame gate__frame--one">
          <img src={lantern} alt="" width={720} height={450} />
          <figcaption>
            <span>If they follow the crystal trail</span>
            The trail runs through the Brass Lantern, where Barker takes his
            orders.
          </figcaption>
        </figure>
        <figure className="gate__frame gate__frame--two">
          <img src={rooftop} alt="" width={720} height={450} />
          <figcaption>
            <span>If they take the rooftops</span>
            The courier’s raven leads them over the slates toward the clock.
          </figcaption>
        </figure>
        <figure className="gate__frame gate__frame--three">
          <img src={bakery} alt="" width={720} height={450} />
          <figcaption>
            <span>If they ask the baker</span>
            Old Hesketh knows who has been buying flour by the cartload.
          </figcaption>
        </figure>
        <div className="gate__quote">
          <span>The Foreman has something to say</span>
          <q>
            The Brass Lantern. That’s all I know. Now lower your voice before
            we both end up in the canal.
          </q>
        </div>
      </div>
      <p className="gate__caption">
        Scenes and dialogue from example campaigns, prepared while the table
        was still talking.
      </p>
    </div>
  );
}

function GateBrand() {
  return (
    <div className="gate__brand">
      <BrandMark />
      Throughline
    </div>
  );
}

export function SignInGate() {
  const { status, config, error, signIn, retry } = useAuth();
  const [email, setEmail] = useState('dev@localhost');
  const [org, setOrg] = useState('local-dev');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (status === 'loading') {
    return (
      <div className="gate">
        <div className="gate__card gate__card--status">
          <span className="spinner" aria-hidden="true" />
          <p>Checking how to sign you in…</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="gate">
        <div className="gate__card">
          <h1 className="gate__title">Sign-in unavailable</h1>
          <p className="gate__lede">
            {error ?? 'We couldn’t reach sign-in. Please try again.'}
          </p>
          <div className="notice notice--error" role="alert">
            <span>
              If you are running locally, leaving the Clerk settings unset will
              start the dashboard with its built-in local provider instead.
            </span>
          </div>
          <button
            type="button"
            className="button button--primary gate__retry"
            onClick={retry}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  async function handleDevSignIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await signIn({ email, org });
    } catch (err) {
      setFormError(
        userFacingError(
          err,
          'Sign-in didn’t work. Check those details and try again.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  if (config?.provider === 'dev') {
    return (
      <div className="gate">
        <GateArt />
        <div className="gate__panel">
          <form className="gate__card" onSubmit={handleDevSignIn}>
            <GateBrand />
            <h1 className="gate__title">
              Welcome to <em>your table.</em>
            </h1>
            <p className="gate__lede">{config.notice}</p>

            <label className="field">
              <span className="field__label">Email</span>
              <input
                className="field__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
                required
              />
            </label>

            <label className="field">
              <span className="field__label">Workspace</span>
              <input
                className="field__input"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                required
              />
              <span className="field__hint">
                Campaigns belong to a workspace. Use the same name to get back
                to your existing ones.
              </span>
            </label>

            {formError ? (
              <div className="notice notice--error" role="alert">
                {formError}
              </div>
            ) : null}

            <button
              type="submit"
              className="button button--primary gate__submit"
              disabled={busy}
            >
              {busy ? 'Signing in…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="gate">
      <GateArt />
      <div className="gate__panel">
        <div className="gate__card">
          <GateBrand />
          <h1 className="gate__title">
            Welcome to <em>the table.</em>
          </h1>
          <p className="gate__lede">
            Throughline listens to your game, remembers your campaign, and
            prepares what could happen next, so you can stay with your players.
          </p>
          <button
            type="button"
            className="button button--primary gate__submit"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setFormError(null);
              try {
                await signIn();
              } catch (err) {
                setFormError(
                  userFacingError(err, 'Sign-in didn’t open. Please try again.'),
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Opening sign-in…' : 'Sign in to Throughline'}
          </button>
          {formError ? (
            <p className="notice notice--error" role="alert">
              {formError}
            </p>
          ) : null}
          <p className="gate__fine">
            New to Throughline? You can create an account on the next screen.
            Your first sessions are free.
          </p>
        </div>
      </div>
    </div>
  );
}
